import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useMathJax } from "../../../hooks/useMathJax";
import { useHint } from "../../../hooks/useHint";
import HintBubble from "../HintBubble";
import { useLearnCtx } from ".././LearnPage";
import SelectionToolbar from "./SelectionToolbar";
import { updateProgressEquationsUsed } from "../../../actions/progress";

/**
 * Renders EDCOMP_EQUATION or EDCOMP_EQN_MULTILINE.
 * Clicking the equation selects it and shows the action toolbar above it.
 * @param {{ item: object, onTooltipOpen: (contentId: string) => void, tooltipOpen: boolean }} props
 */
export default function EquationComp({
  item,
  onTooltipOpen,
  tooltipOpen,
  showHint = false,
  panelOpen = false,
  onExplain,
  onAskAI,
  disableSelectionToolbar = false,
}) {
  const { equation } = item.data ?? {};
  const { openBookmarkModal, highlights, saveHighlight, deleteHighlight } =
    useLearnCtx();
  const hasTooltip = item.data?.tooltip_elements?.length > 0;

  const dispatch = useDispatch();
  const { id: courseId } = useParams();
  const equationsUsed = useSelector(
    (state) => state.ProgressReducer.equationsUsed,
  );

  const ref = useRef(null);
  const containerRef = useRef(null);
  const [selected, setSelected] = useState(false);
  const longPressTimerRef = useRef(null);
  const touchOriginRef = useRef(null);
  const didLongPressRef = useRef(false);
  const [copyDone, setCopyDone] = useState(false);
  const { visible: hintVisible, dismiss: dismissHint } = useHint("eq-tooltip", {
    enabled: showHint,
    value: equationsUsed,
    onDismiss: () => dispatch(updateProgressEquationsUsed(courseId)),
  });

  const eqHighlight = useMemo(
    () => highlights.find((h) => h.paragraphId === item._id) ?? null,
    [highlights, item._id],
  );

  const handleHighlight = useCallback(
    (color) => {
      if (eqHighlight) deleteHighlight(eqHighlight.id);
      saveHighlight({ paragraphId: item._id, color, exact: null });
      setSelected(false);
    },
    [item._id, eqHighlight, saveHighlight, deleteHighlight],
  );

  const handleRemoveHighlight = useCallback(() => {
    if (eqHighlight) deleteHighlight(eqHighlight.id);
    setSelected(false);
  }, [eqHighlight, deleteHighlight]);

  const cleanEquation = (equation ?? "")
    .replace(/\\begin\{align\}/g, "\\begin{align*}")
    .replace(/\\end\{align\}/g, "\\end{align*}")
    // \tag{} on an otherwise empty row is dropped by MathJax; a zero-width
    // hspace gives the row just enough content to force the tag to render.
    .replace(/(\\\\)(\s*)(\\tag\{)/g, "$1$2\\hspace{1mm}$3")
    .replace(/\n(\s*\n)+/g, "\n")
    .trim();

  useMathJax(ref, [cleanEquation]);

  /* Dismiss when pointer-down lands outside this container */
  useEffect(() => {
    if (!selected) return;
    const onOutside = (e) => {
      if (!containerRef.current?.contains(e.target)) setSelected(false);
    };
    document.addEventListener("pointerdown", onOutside, true);
    return () => document.removeEventListener("pointerdown", onOutside, true);
  }, [selected]);

  const handleTouchStart = useCallback((e) => {
    const t = e.touches[0];
    touchOriginRef.current = { x: t.clientX, y: t.clientY };
    didLongPressRef.current = false;
    longPressTimerRef.current = setTimeout(() => {
      setSelected(true);
      didLongPressRef.current = true;
      longPressTimerRef.current = null;
    }, 500);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!touchOriginRef.current) return;
    const t = e.touches[0];
    if (
      Math.abs(t.clientX - touchOriginRef.current.x) > 8 ||
      Math.abs(t.clientY - touchOriginRef.current.y) > 8
    ) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
      touchOriginRef.current = null;
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = null;
    touchOriginRef.current = null;
  }, []);

  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    if (didLongPressRef.current) {
      didLongPressRef.current = false;
      return;
    }
    setSelected((s) => !s);
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setSelected((s) => !s);
    }
    if (e.key === "Escape") setSelected(false);
  }, []);

  const handleExplain = useCallback(() => {
    const stripped = (equation ?? "")
      .trim()
      .replace(/^\$\$/, "")
      .replace(/\$\$$/, "")
      .replace(/^\\\[/, "")
      .replace(/\\\]$/, "");
    const tagMatch = stripped.match(/\\tag\*?\{([^{}]*)\}/);
    const eqNumber = tagMatch ? tagMatch[1].trim() : "";
    const latex = stripped.replace(/\\tag\*?\{[^{}]*\}/g, "").trim();
    setSelected(false);
    onExplain?.("Equation:" + eqNumber + ":" + latex);
  }, [equation, onExplain]);

  const handleAskAI = useCallback(() => {
    setSelected(false);
    onAskAI?.(equation ?? "");
  }, [equation, onAskAI]);

  /* Strip delimiters and \tag{...}, then wrap in the right environment */
  const copyLatex = useCallback(
    async (e) => {
      e.stopPropagation();
      let latex = (equation ?? "")
        .trim()
        .replace(/^\$\$/, "")
        .replace(/\$\$$/, "")
        .replace(/^\\\[/, "")
        .replace(/\\\]$/, "")
        .replace(/\\tag\{[^{}]*\}/g, "")
        .trim();

      if (/\\begin\{/.test(latex)) {
        // already has an explicit environment — leave as-is
      } else if (latex.includes("\\\\")) {
        latex = `\\begin{align}\n${latex}\n\\end{align}`;
      } else {
        latex = `\\begin{equation}\n${latex}\n\\end{equation}`;
      }

      try {
        await navigator.clipboard.writeText(latex);
        setCopyDone(true);
        setTimeout(() => setCopyDone(false), 1500);
      } catch {
        /* clipboard access denied */
      }
    },
    [equation],
  );

  return (
    <div
      ref={containerRef}
      id={"eq:" + String(item._id)}
      className={[
        "ct__equation-container",
        selected ? "ct__equation-container--selected" : "",
        eqHighlight ? `ct__equation-container--hl-${eqHighlight.color}` : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-content-id={item._id}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onContextMenu={handleContextMenu}
      role="button"
      tabIndex={0}
      aria-label="Equation — click or long-press to select"
    >
      {selected && !disableSelectionToolbar && (
        <SelectionToolbar
          onCopy={copyLatex}
          copyDone={copyDone}
          onBookmark={() =>
            openBookmarkModal(item._id, item.title, "eq:" + item._id)
          }
          onHighlight={handleHighlight}
          onRemoveHighlight={handleRemoveHighlight}
          onExplain={onExplain ? handleExplain : undefined}
          onAskAI={onAskAI ? handleAskAI : undefined}
        />
      )}

      <div
        className={`ct__equation-wrap${tooltipOpen ? " ct__equation-wrap--active" : ""}`}
      >
        <div
          ref={ref}
          className="ct__equation"
          dangerouslySetInnerHTML={{ __html: cleanEquation }}
        />

        {hasTooltip && (
          <>
            <button
              className={`ct__eq-gutter-icon${tooltipOpen ? " ct__eq-gutter-icon--active" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                dismissHint();
                onTooltipOpen(item._id, e);
              }}
              title="Equation details"
              aria-label="Equation details"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <circle
                  cx="8"
                  cy="8"
                  r="7"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <text
                  x="8"
                  y="12"
                  textAnchor="middle"
                  fontSize="10"
                  fill="currentColor"
                  fontFamily="sans-serif"
                >
                  ?
                </text>
              </svg>
            </button>
            {hintVisible && !panelOpen && (
              <HintBubble
                text="Use this widget to see more information about the equation"
                onDismiss={dismissHint}
                anchor="eq-right"
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
